/**
 * ADM-06 课程 SKU 编辑对话框（subject-course-sku:SC-18~SC-37 前端）。
 *
 * 分区表单：教学信息 / 收费 / 课时与扣减 / 分班配置。
 * - 扣减单位只读恒按次（PER_SESSION）
 * - 标价表单用「元」，提交转分（×100），回填转元（÷100）
 * - 底部：暂存（保存为草稿/已下架）、上架（二次确认后先保存再 publish）、取消
 * - 有未保存内容时确认离开
 *
 * 仅处理 DRAFT / OFF_SHELF 可编辑状态；已上架（ON_SHELF）编辑入口由列表页禁用。
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
} from 'antd';
import { DraggableModal } from '../../components/DraggableModal';
import { useDraggableModal } from '../../components/useDraggableModal';
import type { CourseSkuItem, CreateCourseSku, UpdateCourseSku } from '../../api/course-skus.api';
import {
  deductionUnitLabel,
  gradeStageLabel,
  toSelectOptions,
} from '../../api/labels';

/** 表单值（listPrice 为「元」，提交时由父组件转分；O-14 起无收费模式字段）。 */
export interface SkuFormValues {
  code: string;
  name: string;
  subjectId: string;
  /** 校区归属（按校区办学）：undefined = 未选（提交转 null 机构通用） */
  campusId?: string;
  gradeStage: CourseSkuItem['gradeStage'];
  textbook: string;
  listPrice: number;
  entitlementQuantity: number;
  trialSessionCount: number;
  deductionUnit: CourseSkuItem['deductionUnit'];
  validityDays: number;
  maxClassCapacity: number;
  maxClassCount: number;
}

export interface SkuEditModalProps {
  /** 是否打开 */
  open: boolean;
  /** 当前编辑的 SKU（null 为新建） */
  editing: CourseSkuItem | null;
  /** 科目下拉选项 */
  subjectOptions: Array<{ value: string; label: string }>;
  /** 校区下拉选项（按校区办学：留空 = 机构通用） */
  campusOptions: Array<{ value: string; label: string }>;
  /** 暂存（新建/编辑保存，返回最新 SKU） */
  onPersist: (values: SkuFormValues, editing: CourseSkuItem | null) => Promise<CourseSkuItem>;
  /** 上架（先保存后 publish） */
  onPublish: (id: string) => Promise<void>;
  /** 关闭 */
  onClose: () => void;
}

/** 新建 SKU 的表单默认值。 */
const DEFAULT_VALUES: Partial<SkuFormValues> = {
  trialSessionCount: 2,
  deductionUnit: 'PER_SESSION',
  validityDays: 365,
  maxClassCapacity: 12,
  maxClassCount: 2,
};

/** 由已存在 SKU 回填表单值（listPrice 分转元）。 */
function valuesFromSku(sku: CourseSkuItem): SkuFormValues {
  return {
    code: sku.code,
    name: sku.name,
    subjectId: sku.subjectId,
    campusId: sku.campusId ?? undefined,
    gradeStage: sku.gradeStage,
    textbook: sku.textbook,
    listPrice: sku.listPrice / 100,
    entitlementQuantity: sku.entitlementQuantity,
    trialSessionCount: sku.trialSessionCount,
    deductionUnit: sku.deductionUnit,
    validityDays: sku.validityDays,
    maxClassCapacity: sku.maxClassCapacity,
    maxClassCount: sku.maxClassCount,
  };
}

export function SkuEditModal({
  open,
  editing,
  subjectOptions,
  campusOptions,
  onPersist,
  onPublish,
  onClose,
}: SkuEditModalProps) {
  const [form] = Form.useForm<SkuFormValues>();
  const modal = useDraggableModal();
  const [submitting, setSubmitting] = useState(false);

  // 打开时回填表单值
  useEffect(() => {
    if (open) {
      if (editing) {
        form.setFieldsValue(valuesFromSku(editing));
      } else {
        form.resetFields();
        form.setFieldsValue(DEFAULT_VALUES as SkuFormValues);
      }
    }
  }, [open, editing, form]);

  /** 暂存：校验并保存，成功后关闭。 */
  const handleSaveDraft = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await onPersist(values, editing);
    } finally {
      setSubmitting(false);
    }
  };

  /** 上架：二次确认后先保存再 publish。 */
  const handlePublish = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const saved = await onPersist(values, editing);
      await onPublish(saved.id);
    } finally {
      setSubmitting(false);
    }
  };

  /** 取消：有未保存内容时确认离开。 */
  const handleCancel = () => {
    const touched = form.isFieldsTouched();
    if (touched) {
      modal.confirm({
        title: '确认离开？',
        content: '当前有未保存的内容，离开后将丢失修改。',
        okText: '离开',
        okButtonProps: { danger: true },
        cancelText: '继续编辑',
        onOk: () => onClose(),
      });
    } else {
      onClose();
    }
  };

  return (
    <DraggableModal
      open={open}
      title={editing ? '编辑课程SKU' : '新增课程SKU'}
      width={720}
      onCancel={handleCancel}
      destroyOnHidden
      footer={
        <Space>
          <Button onClick={handleCancel}>取消</Button>
          <Button loading={submitting} onClick={() => void handleSaveDraft()}>
            暂存
          </Button>
          <Popconfirm
            title="确认上架该课程SKU？"
            description="上架后将可被课程商品引用"
            onConfirm={() => handlePublish()}
            okText="确定上架"
            cancelText="取消"
          >
            <Button type="primary" loading={submitting}>
              上架
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      {editing && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="修改 SKU 不影响已生成的历史订单快照"
        />
      )}
      <Form form={form} layout="vertical">
        {/* 教学信息分区 */}
        <Space size={16} wrap>
          <Form.Item
            label="SKU编码"
            name="code"
            rules={[{ required: true, message: '请输入编码' }]}
          >
            <Input style={{ width: 200 }} disabled={!!editing} />
          </Form.Item>
          <Form.Item
            label="名称"
            name="name"
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input style={{ width: 240 }} />
          </Form.Item>
        </Space>
        <Space size={16} wrap>
          <Form.Item
            label="科目"
            name="subjectId"
            tooltip={editing ? '编码与科目创建后不可修改' : undefined}
            rules={[{ required: true, message: '请选择科目' }]}
          >
            <Select
              style={{ width: 160 }}
              options={subjectOptions}
              placeholder="选择科目"
              disabled={!!editing}
            />
          </Form.Item>
          <Form.Item
            label="年级/学段"
            name="gradeStage"
            rules={[{ required: true, message: '请选择学段' }]}
          >
            <Select style={{ width: 160 }} options={toSelectOptions(gradeStageLabel)} placeholder="选择学段" />
          </Form.Item>
          <Form.Item
            label="教材版本"
            name="textbook"
            rules={[{ required: true, message: '请输入教材版本' }]}
          >
            <Input style={{ width: 160 }} />
          </Form.Item>
          <Form.Item
            label="校区归属"
            name="campusId"
            tooltip="选填：留空 = 机构通用（全校区可见）"
          >
            <Select
              style={{ width: 160 }}
              allowClear
              options={campusOptions}
              placeholder="机构通用"
              data-testid="sku-campus-select"
            />
          </Form.Item>
        </Space>

        {/* 收费分区（O-14 起无收费模式字段，SKU 回归纯教学标准） */}
        <Space size={16} wrap>
          <Form.Item
            label="标价（元）"
            name="listPrice"
            rules={[{ required: true, message: '请输入标价' }]}
          >
            <InputNumber style={{ width: 160 }} min={0} precision={2} />
          </Form.Item>
          <Form.Item
            label="课时数"
            name="entitlementQuantity"
            rules={[{ required: true, message: '请输入课时数' }]}
          >
            <InputNumber style={{ width: 140 }} min={1} />
          </Form.Item>
        </Space>

        {/* 课时与扣减分区 */}
        <Space size={16} wrap>
          <Form.Item
            label="试课赠送次数"
            name="trialSessionCount"
            rules={[{ required: true, message: '请输入试课赠送次数' }]}
          >
            <InputNumber style={{ width: 140 }} min={1} max={3} />
          </Form.Item>
          <Form.Item label="扣减单位" name="deductionUnit">
            <Select
              style={{ width: 160 }}
              disabled
              options={toSelectOptions(deductionUnitLabel)}
            />
          </Form.Item>
          <Form.Item
            label="有效期（天）"
            name="validityDays"
            rules={[{ required: true, message: '请输入有效天数' }]}
          >
            <InputNumber style={{ width: 140 }} min={1} />
          </Form.Item>
        </Space>

        {/* 分班配置分区 */}
        <Space size={16} wrap>
          <Form.Item
            label="每班最多人数"
            name="maxClassCapacity"
            tooltip="筛选分班时按此人数自动分组"
            rules={[{ required: true, message: '请设置每班最多人数' }]}
          >
            <InputNumber style={{ width: 140 }} min={1} max={100} />
          </Form.Item>
          <Form.Item
            label="最多班级数"
            name="maxClassCount"
            tooltip="筛选分班时自动建议的班级数上限"
            rules={[{ required: true, message: '请设置最多班级数' }]}
          >
            <InputNumber style={{ width: 140 }} min={1} max={20} />
          </Form.Item>
        </Space>
      </Form>
    </DraggableModal>
  );
}

/** 由表单值构造新建请求体（标价元转分）。 */
export function toCreateDto(values: SkuFormValues): CreateCourseSku {
  return {
    subjectId: values.subjectId,
    code: values.code,
    ...toEditableFields(values),
  };
}

/** 由表单值构造编辑请求体（不含编码与科目，附乐观锁版本号）。 */
export function toUpdateDto(values: SkuFormValues, version: number): UpdateCourseSku {
  return { ...toEditableFields(values), version };
}

/** 新建与编辑共用的可编辑字段（标价元转分；O-14 起请求不携带 chargeMode）。 */
function toEditableFields(values: SkuFormValues): Omit<CreateCourseSku, 'subjectId' | 'code'> {
  return {
    // 校区归属随编辑整体覆盖（undefined 视为未选 → null 机构通用，对齐商品编辑先例）
    campusId: values.campusId ?? null,
    name: values.name,
    gradeStage: values.gradeStage,
    textbook: values.textbook,
    listPrice: Math.round(values.listPrice * 100),
    entitlementQuantity: values.entitlementQuantity,
    trialSessionCount: values.trialSessionCount,
    validityDays: values.validityDays,
    maxClassCapacity: values.maxClassCapacity,
    maxClassCount: values.maxClassCount,
  };
}
